import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';

export interface UserProfile {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    roleId: number;
    department?: string;
    isActive: boolean;
    lastLogin?: Date;
    createdAt?: Date;
    profilePhoto?: string;
    profilePhotoMime?: string;
    phone?: string;
    jobTitle?: string;
    bio?: string;
}

export interface UpdateProfileRequest {
    firstName?: string;
    lastName?: string;
    email?: string;
    department?: string;
    phone?: string;
    jobTitle?: string;
    bio?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ProfileService {
    private apiUrl = environment.apiUrl;

    private profileSubject = new BehaviorSubject<UserProfile | null>(null);
    public profile$ = this.profileSubject.asObservable();

    // Shared photo URL — null means no photo (show default avatar)
    private profilePhotoUrlSubject = new BehaviorSubject<string | null>(null);
    public profilePhotoUrl$ = this.profilePhotoUrlSubject.asObservable();

    setProfilePhotoUrl(url: string | null): void {
        this.profilePhotoUrlSubject.next(url);
    }

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) {}

    private getAuthHeaders(): HttpHeaders {
        const token = this.authService.getToken();
        return new HttpHeaders({
            'Authorization': `Bearer ${token}`
        });
    }

    getMyProfile(): Observable<UserProfile> {
        return this.http.get<UserProfile>(`${this.apiUrl}/profile/me`, {
            headers: this.getAuthHeaders()
        }).pipe(
            tap(profile => this.profileSubject.next(profile)),
            catchError(error => {
                console.error('Get profile error:', error);
                return throwError(error.error?.error || 'Failed to load profile');
            })
        );
    }

    updateProfile(data: UpdateProfileRequest): Observable<UserProfile> {
        return this.http.put<UserProfile>(`${this.apiUrl}/profile/me`, data, {
            headers: this.getAuthHeaders()
        }).pipe(
            tap(profile => this.profileSubject.next(profile)),
            catchError(error => {
                console.error('Update profile error:', error);
                return throwError(error.error?.error || 'Failed to update profile');
            })
        );
    }

    uploadProfilePhoto(file: File): Observable<any> {
        const formData = new FormData();
        formData.append('photo', file);

        return this.http.post<any>(`${this.apiUrl}/profile/photo`, formData, {
            headers: new HttpHeaders({
                'Authorization': `Bearer ${this.authService.getToken()}`
            })
        }).pipe(
            tap(response => {
                // Update profile with new photo
                const currentProfile = this.profileSubject.value;
                if (currentProfile) {
                    this.profileSubject.next({
                        ...currentProfile,
                        profilePhoto: response.profilePhoto,
                        profilePhotoMime: response.profilePhotoMime
                    });
                }
            }),
            catchError(error => {
                console.error('Upload photo error:', error);
                return throwError(error.error?.error || 'Failed to upload photo');
            })
        );
    }

    deleteProfilePhoto(): Observable<any> {
        return this.http.delete<any>(`${this.apiUrl}/profile/photo`, {
            headers: this.getAuthHeaders()
        }).pipe(
            tap(() => {
                // Update profile to remove photo
                const currentProfile = this.profileSubject.value;
                if (currentProfile) {
                    this.profileSubject.next({
                        ...currentProfile,
                        profilePhoto: undefined,
                        profilePhotoMime: undefined
                    });
                }
            }),
            catchError(error => {
                console.error('Delete photo error:', error);
                return throwError(error.error?.error || 'Failed to delete photo');
            })
        );
    }

    getProfilePhotoUrl(userId?: number): string {
        const id = userId || this.profileSubject.value?.id;
        if (id) {
            return `${this.apiUrl}/profile/photo/${id}?t=${Date.now()}`;
        }
        return 'assets/images/users/profile.png';
    }

    getProfilePhotoBlobUrl(userId?: number): Observable<string> {
        const id = userId || this.profileSubject.value?.id;
        console.log('[ProfileService] getProfilePhotoBlobUrl — userId resolved to:', id);
        return this.http.get(`${this.apiUrl}/profile/photo/${id}`, {
            headers: this.getAuthHeaders(),
            responseType: 'blob'
        }).pipe(
            map(blob => {
                console.log('[ProfileService] Photo blob received — size:', blob.size, 'type:', blob.type);
                return URL.createObjectURL(blob);
            }),
            catchError((err) => {
                console.error('[ProfileService] Photo blob fetch error:', err);
                return throwError('Failed to load profile photo');
            })
        );
    }

    changePassword(currentPassword: string, newPassword: string): Observable<void> {
        const profile = this.profileSubject.value;
        if (!profile) {
            return throwError('Profile not loaded');
        }
        return this.http.post<any>(`${this.apiUrl}/users/${profile.id}/change-password`,
            { currentPassword, newPassword },
            { headers: this.getAuthHeaders() }
        ).pipe(
            // The API revokes all older tokens and returns a fresh one for this session.
            tap(response => { if (response?.token) { this.authService.replaceToken(response.token); } }),
            map(() => undefined),
            catchError(error => {
                console.error('Change password error:', error);
                return throwError(error.error?.error || 'Failed to change password');
            })
        );
    }

    getCurrentProfile(): UserProfile | null {
        return this.profileSubject.value;
    }
}
